
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Share2, Users, Trash2, Eye, Edit, Copy } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DocumentSharingProps {
  documentId: string;
  organizationId?: string;
}

interface DocumentShare {
  id: string;
  user_id: string;
  permission: 'read' | 'write' | 'admin';
  shared_at: string;
  profile?: {
    full_name: string | null;
    email: string | null;
  } | null;
}

export const DocumentSharing = ({ documentId, organizationId }: DocumentSharingProps) => {
  const { toast } = useToast();
  const [shares, setShares] = useState<DocumentShare[]>([]);
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const [isLoading, setIsLoading] = useState(false);
  const [shareableLink, setShareableLink] = useState("");

  useEffect(() => {
    loadShares();
    generateShareableLink();
  }, [documentId]);

  const loadShares = async () => {
    try {
      // First get shares
      const { data: sharesData, error: sharesError } = await supabase
        .from('document_shares')
        .select('*')
        .eq('document_id', documentId);

      if (sharesError) throw sharesError;

      // Then get profile data for each share
      const sharesWithProfiles = await Promise.all(
        (sharesData || []).map(async (share) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', share.user_id)
            .single();

          return {
            ...share,
            permission: share.permission as 'read' | 'write' | 'admin',
            profile
          };
        })
      );

      setShares(sharesWithProfiles);
    } catch (error: any) {
      console.error('Error loading shares:', error);
    }
  };

  const generateShareableLink = () => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/documents/${documentId}?shared=true`;
    setShareableLink(link);
  };

  const shareDocument = async () => {
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter an email address to share with.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // First, check if user exists
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email.trim())
        .single();

      if (profileError || !profile) {
        toast({
          title: "User Not Found",
          description: "The user must have a CSO Agent account to share documents.",
          variant: "destructive",
        });
        return;
      }

      // Share the document
      const { error } = await supabase
        .from('document_shares')
        .insert({
          document_id: documentId,
          user_id: profile.id,
          permission,
        });

      if (error) throw error;

      toast({
        title: "Document Shared",
        description: `Document shared with ${email} successfully.`,
      });

      setEmail("");
      loadShares();
    } catch (error: any) {
      console.error('Error sharing document:', error);
      toast({
        title: "Sharing Failed",
        description: error.message || "Failed to share document.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const removeShare = async (shareId: string) => {
    try {
      const { error } = await supabase
        .from('document_shares')
        .delete()
        .eq('id', shareId);

      if (error) throw error;

      toast({
        title: "Access Removed",
        description: "User access has been removed successfully.",
      });

      loadShares();
    } catch (error: any) {
      console.error('Error removing share:', error);
      toast({
        title: "Error",
        description: "Failed to remove access.",
        variant: "destructive",
      });
    }
  };

  const copyShareableLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast({
      title: "Link Copied",
      description: "Shareable link copied to clipboard.",
    });
  };

  return (
    <div className="space-y-6">
      {/* Organization Sharing */}
      {organizationId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Organization Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">
              This document is automatically shared with all members of your organization.
            </p>
            <Badge variant="secondary">All Organization Members</Badge>
          </CardContent>
        </Card>
      )}

      {/* Share with Individual Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share with Users
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@company.com"
              />
            </div>
            <div>
              <Label>Permission</Label>
              <Select value={permission} onValueChange={(value: 'read' | 'write') => setPermission(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Read Only</SelectItem>
                  <SelectItem value="write">Read & Write</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={shareDocument} disabled={isLoading}>
            {isLoading ? "Sharing..." : "Share Document"}
          </Button>
        </CardContent>
      </Card>

      {/* Shareable Link */}
      <Card>
        <CardHeader>
          <CardTitle>Shareable Link</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input value={shareableLink} readOnly className="flex-1" />
            <Button onClick={copyShareableLink} variant="outline" size="icon">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Anyone with this link can view the document (if they have a CSO Agent account)
          </p>
        </CardContent>
      </Card>

      {/* Current Shares */}
      {shares.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Shared With</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {shares.map((share) => (
                <div key={share.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium">{share.profile?.full_name || share.profile?.email || 'Anonymous'}</p>
                      <p className="text-sm text-gray-500">{share.profile?.email}</p>
                    </div>
                    <Badge variant={share.permission === 'write' ? 'default' : 'secondary'}>
                      {share.permission === 'write' ? (
                        <><Edit className="h-3 w-3 mr-1" /> Write</>
                      ) : (
                        <><Eye className="h-3 w-3 mr-1" /> Read</>
                      )}
                    </Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeShare(share.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
